/**
 * LO QUE SE DICE DEBAJO DE ALGO: reacciones y comentarios.
 *
 * POR QUÉ EXISTE. Esto estaba escrito DOS VECES, igual, en `WallCard` y en
 * `ActivityCard`, y el muro de memes iba a ser la tercera. Tres copias de una
 * conversación son tres lugares donde arreglar el día que el botón de borrar
 * no se vea, o que Enter deje de mandar. Ya había señales: el comentario de
 * «quien administra el club» sigue escrito en las dos aunque en los memes
 * quien puede es la administración de la plataforma.
 *
 * RECIBE LAS PIEZAS Y NO LA TARJETA. Antes cada copia recibía su propio tipo
 * —`WallEntry`, `ActivityEntry`— y por eso no se podían compartir. Acá entran
 * la lista de reacciones, la de comentarios y qué hacer: lo único que estas
 * dos cosas necesitan saber.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  COMMENT_MAX,
  WALL_REACTIONS,
  type MatchComment,
  type WallReactionCount,
} from '@tennisup/contracts';

import { Avatar } from './Avatar';
import { assetUrl } from '../lib/api';

/**
 * SE DIBUJAN LAS QUE TIENEN ALGUIEN Y ADEMÁS TODAS LAS POSIBLES, pero de dos
 * maneras: las que ya tienen gente van con su número; el resto son los botones
 * para poner una. Sin lo segundo no hay forma de reaccionar primero; sin lo
 * primero, un partido con veinte 🔥 se ve igual que uno sin nada.
 */
export function Reactions({
  reactions,
  onToggle,
}: {
  reactions: readonly WallReactionCount[];
  onToggle: (emoji: string, on: boolean) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const puestas = new Map(reactions.map((r) => [r.emoji, r]));

  return (
    <p className="wall-reactions" aria-label={t('wall.react')}>
      {WALL_REACTIONS.map((emoji) => {
        const puesta = puestas.get(emoji);
        const mine = puesta?.mine ?? false;
        return (
          <button
            key={emoji}
            type="button"
            className={mine ? 'wall-react wall-react-mine' : 'wall-react'}
            // `aria-pressed` y no solo una clase: quien no ve el color tiene
            // que poder saber si YA reaccionó, que es lo que decide si el
            // toque pone o quita.
            aria-pressed={mine}
            onClick={() => onToggle(emoji, !mine)}
          >
            <span aria-hidden="true">{emoji}</span>
            {puesta !== undefined && puesta.count > 0 && (
              <span className="wall-react-count">{puesta.count}</span>
            )}
          </button>
        );
      })}
    </p>
  );
}

export function CommentThread({
  id,
  comments,
  onComment,
  onDelete,
}: {
  /** Para que la etiqueta del campo apunte a ESTE cuadro y no a otro de la lista. */
  id: string;
  comments: readonly MatchComment[];
  onComment: (body: string) => Promise<void>;
  onDelete: (comentario: MatchComment) => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation();
  const [texto, setTexto] = useState('');
  const [mandando, setMandando] = useState(false);

  async function mandar(): Promise<void> {
    const limpio = texto.trim();
    if (limpio === '' || mandando) return;
    setMandando(true);
    try {
      await onComment(limpio);
      setTexto('');
    } finally {
      setMandando(false);
    }
  }

  return (
    <section className="wall-comments">
      {comments.length > 0 && (
        <ul className="wall-comment-list">
          {comments.map((comentario) => (
            <li key={comentario.id} className="wall-comment">
              <Avatar
                url={assetUrl(comentario.authorAvatarPath)}
                name={comentario.authorName}
                size={28}
              />
              <span className="wall-comment-text">
                <span className="wall-comment-who">{comentario.authorName}</span>
                <span>{comentario.body}</span>
              </span>
              {/* EL BOTÓN DE BORRAR SALE PORQUE EL SERVIDOR DIJO QUE SE PUEDE.
                  Quién es «se puede» cambia según de qué cuelgue el comentario
                  —el club en un partido, la plataforma en un meme— y por eso
                  la pantalla no lo decide: solo obedece el permiso que vino. */}
              {comentario.canDelete && (
                <button
                  type="button"
                  className="link danger wall-comment-x"
                  aria-label={t('wall.deleteComment')}
                  onClick={() => void onDelete(comentario)}
                >
                  <span aria-hidden="true">✕</span>
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="wall-comment-box">
        <label className="visually-hidden" htmlFor={`comment-${id}`}>
          {t('wall.comment')}
        </label>
        <input
          id={`comment-${id}`}
          type="text"
          autoComplete="off"
          maxLength={COMMENT_MAX}
          placeholder={t('wall.commentPlaceholder')}
          value={texto}
          onChange={(event) => setTexto(event.target.value)}
          // Enter manda: es un renglón, no un formulario. Obligar a apuntarle
          // al botón después de escribir es el paso donde nadie comenta.
          onKeyDown={(event) => {
            if (event.key === 'Enter') void mandar();
          }}
        />
        <button
          type="button"
          className="link"
          disabled={texto.trim() === '' || mandando}
          onClick={() => void mandar()}
        >
          {t('wall.send')}
        </button>
      </div>
    </section>
  );
}
